import { Button } from '../ui/button';
import { ArrowRight, User, Building2, Workflow } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';
import exponentialGrowthBg from '@/assets/exponential-growth-bg.png';
import heroBlackMarble from '@/assets/hero-the-between.jpg';
import heroBlackMarbleMobile from '@/assets/hero-the-between-mobile.jpg';
import shaunCollett from '@/assets/shaun-collett.png';
import dimensionSelf from '@/assets/dimension-self.jpg';
import dimensionWork from '@/assets/dimension-work.jpg';
import dimensionRelationships from '@/assets/dimension-relationships.jpg';
import dimensionLegacy from '@/assets/dimension-legacy.jpg';
import ctaCoaching from '@/assets/cta-coaching.jpg';

const handleScheduleCall = () => {
  window.open('https://calendly.com/kentfrazier/30-minutes', '_blank');
};

// ============ Hero - simplified: one voice, one line, one action ============
const Hero = () => (
  <section className="relative overflow-hidden flex flex-col min-h-[calc(100svh-6rem)] sm:min-h-[88vh]">
    <div
      className="absolute inset-0 bg-no-repeat sm:hidden brightness-110 contrast-110"
      style={{
        backgroundImage: `url(${heroBlackMarbleMobile})`,
        backgroundPosition: 'center 60%',
        backgroundSize: '155% auto',
      }}
    />
    <div
      className="absolute inset-0 bg-cover bg-center bg-no-repeat hidden sm:block"
      style={{ backgroundImage: `url(${heroBlackMarble})` }}
    />
    <div className="absolute inset-0 sm:hidden" style={{ backgroundColor: 'hsl(20 30% 6% / 0.35)' }} aria-hidden="true" />
    <div className="absolute inset-0 hidden sm:block" style={{ backgroundColor: 'hsl(20 30% 6% / 0.55)' }} aria-hidden="true" />
    <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/10 to-black/45" aria-hidden="true" />
    <div className="absolute inset-0 sm:hidden" style={{ background: 'radial-gradient(ellipse at center 60%, hsl(20 30% 6% / 0.82) 0%, transparent 55%)' }} aria-hidden="true" />

    <div className="relative z-10 flex-1 flex items-center px-6 sm:px-8 md:px-12 lg:px-16 pt-20 pb-16 sm:pt-40 sm:pb-32">
      <div className="container-mobile max-w-4xl mx-auto w-full text-center">
        <div className="flex items-center justify-center mb-10 sm:mb-8">
          <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
            Leadership in the Age of AI
          </p>
        </div>
        <h1
          className="text-background text-[2.5rem] sm:text-6xl lg:text-7xl mb-10 sm:mb-8 leading-[1.05]"
          style={{
            textWrap: 'balance',
            textShadow: '0 2px 24px hsl(var(--foreground) / 0.9), 0 0 8px hsl(var(--foreground) / 0.7)',
          }}
        >
          The edge is{' '}
          <span
            className="italic font-display"
            style={{
              color: '#C45C26',
              textShadow: '0 2px 18px hsl(var(--foreground) / 0.95), 0 0 10px hsl(var(--foreground) / 0.85)',
            }}
          >
            human
          </span>
          .
        </h1>
        <p className="text-background/90 text-lg sm:text-2xl leading-relaxed max-w-2xl mx-auto font-normal drop-shadow mb-12 sm:mb-10">
          Develop your capacity.
        </p>
        <button
          onClick={handleScheduleCall}
          className="inline-flex items-center gap-3 border border-background/40 hover:border-primary hover:bg-primary text-background px-7 sm:px-9 py-3.5 sm:py-4 text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase rounded-full transition-all duration-300 group"
        >
          Begin a Conversation
          <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    </div>
  </section>
);

// ============ The Moment - light editorial ============
const TheMoment = () => {
  const heading = useScrollReveal<HTMLDivElement>();
  const body = useScrollReveal<HTMLDivElement>();
  const argument = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-background py-24 sm:py-32 lg:py-40 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          background:
            'radial-gradient(ellipse at 12% 15%, hsl(var(--primary) / 0.09) 0%, transparent 55%), radial-gradient(ellipse at 88% 85%, hsl(var(--sage) / 0.09) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />
      <div className="container-mobile relative z-10 max-w-5xl mx-auto">
        <div
          ref={heading.ref}
          className={`max-w-3xl mb-12 transition-all duration-1000 ${
            heading.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-12 bg-primary" aria-hidden="true" />
            <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
              The Moment
            </p>
          </div>
          <h2
            className="text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
            style={{ textWrap: 'balance' }}
          >
            Every leader just became a{' '}
            <span className="text-primary italic">technology leader.</span>
          </h2>
        </div>

        <div
          ref={body.ref}
          className={`max-w-2xl space-y-6 transition-all duration-1000 delay-150 ${
            body.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <p className="text-foreground/85 text-lg sm:text-xl leading-relaxed font-normal">
            It happened in about eighteen months, without a decision and without training. A head of
            finance is now making calls about what work a model should touch. A clinical director is
            deciding what stays human. A founder is rebuilding a team around tools that did not exist
            when she hired them.
          </p>
          <p className="text-foreground/85 text-lg sm:text-xl leading-relaxed font-normal">
            The decisions arrive whether or not anyone is ready. What goes to the machine. What stays
            with a person. What the team is allowed to do, and what that permission does to their
            craft. The pressure is real and it is quiet, and almost nobody has language for what it is
            doing to them.
          </p>
          <p className="font-display italic text-2xl sm:text-3xl text-foreground leading-snug pt-2">
            That is the work in front of us.
          </p>
        </div>

        <div
          ref={argument.ref}
          className={`max-w-3xl mt-20 pt-16 border-t border-border/60 transition-all duration-1000 delay-150 ${
            argument.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <p
            className="font-display text-3xl sm:text-4xl lg:text-5xl text-foreground leading-[1.1] mb-8"
            style={{ textWrap: 'balance' }}
          >
            Technical capability is the thing being commoditized.
          </p>
          <div className="max-w-2xl space-y-6">
            <p className="text-foreground/85 text-lg sm:text-xl leading-relaxed font-normal">
              Every release puts more of it in more hands for less money. A skill that took a decade
              to build is now a prompt away. Investing further there buys a smaller edge each
              quarter, and the curve is steepening.
            </p>
            <p className="text-foreground/85 text-lg sm:text-xl leading-relaxed font-normal">
              Human capacity moves the other way. Presence when the room is frightened. Judgment
              with no precedent to lean on. The ability to hold two true things at once long enough
              to find the third. These compound with practice, and no release schedule erodes them.
            </p>
            <p className="text-foreground/85 text-lg sm:text-xl leading-relaxed font-normal">
              This is uncomfortable to hear if you have spent a career building technical mastery.
              Sit with it anyway. The people who move first on this will be the ones others follow.
            </p>
          </div>
          <p className="font-display italic text-xl sm:text-2xl text-primary leading-snug mt-10">
            That is the edge. It is human.
          </p>
        </div>
      </div>
    </section>
  );
};

// ============ Featured Testimonial - asymmetric editorial spread ============
const FeaturedTestimonial = () => {
  const quote = useScrollReveal<HTMLDivElement>();
  const attribution = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-secondary/40 text-foreground py-24 sm:py-32 lg:py-40 overflow-hidden">
      {/* Soft painterly wash behind the spread */}
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          background:
            'radial-gradient(ellipse at 30% 40%, hsl(var(--primary) / 0.08) 0%, transparent 55%), radial-gradient(ellipse at 70% 60%, hsl(var(--sage) / 0.08) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage:
            'linear-gradient(to right, hsl(var(--foreground)) 1px, transparent 1px)',
          backgroundSize: '120px 100%',
        }}
        aria-hidden="true"
      />

      <div className="container-mobile relative z-10 max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 items-center">
          {/* Quote side */}
          <div
            ref={quote.ref}
            className={`lg:col-span-7 relative transition-all duration-1000 ${
              quote.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <div className="flex items-center gap-4 mb-8">
              <span className="h-px w-12 bg-primary" aria-hidden="true" />
              <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
                In Their Words
              </p>
            </div>

            <blockquote>
              <p
                className="font-display text-xl sm:text-2xl lg:text-4xl text-foreground leading-snug italic"
                style={{ textWrap: 'balance' }}
              >
                I've had the life changing opportunity to study and grow alongside Kent across multiple companies and capacities. His impact is profound and expansive to life, not just work.
              </p>
              <div className="w-20 h-px bg-sage mt-10" aria-hidden="true" />
            </blockquote>
          </div>

          {/* Attribution side */}
          <div
            ref={attribution.ref}
            className={`lg:col-span-5 flex flex-col items-center lg:items-start gap-8 transition-all duration-1000 delay-150 ${
              attribution.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <div className="relative">
              {/* Soft painterly wash behind the portrait */}
              <div
                className="absolute -inset-6 sm:-inset-8 rounded-full blur-2xl"
                style={{ background: 'hsl(var(--primary) / 0.12)' }}
                aria-hidden="true"
              />
              {/* Hairline terracotta ring */}
              <div
                className="absolute inset-[-5px] sm:inset-[-6px] rounded-full"
                style={{ border: '1px solid hsl(var(--primary) / 0.25)' }}
                aria-hidden="true"
              />
              <img
                src={shaunCollett}
                alt="Shaun Collett"
                className="relative w-36 h-36 sm:w-44 sm:h-44 lg:w-56 lg:h-56 rounded-full object-cover ring-[3px] ring-background"
                style={{ boxShadow: '0 20px 40px -12px hsl(var(--primary) / 0.22)' }}
              />
            </div>

            <div className="text-center lg:text-left space-y-2">
              <cite className="not-italic font-display text-xl sm:text-2xl font-semibold text-foreground block">
                Shaun Collett
              </cite>
              <p className="text-primary font-semibold tracking-[0.2em] uppercase text-xs">
                Global Forward Deployment Engineering Leader — Generative and Agentic AI
              </p>
              <p className="text-foreground/70 text-sm italic">Amazon Web Services</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// NOTE: Confirm these figures with the source data before publishing.
// ============ Proof Band - quiet compact metrics ============
const proofPoints = [
  { figure: '26%', label: 'Increase in thriving' },
  { figure: '91', label: 'Net promoter score' },
  { figure: '1,000+', label: 'Engineers and leaders developed' },
];

const ProofBand = () => {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();
  return (
    <section className="bg-card border-y border-border/60 py-14 sm:py-16">
      <div
        ref={ref}
        className={`container-mobile max-w-5xl mx-auto transition-all duration-1000 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 sm:gap-8 text-center">
          {proofPoints.map((p) => (
            <div key={p.label} className="space-y-3">
              <p className="font-display text-primary text-4xl sm:text-5xl leading-none">
                {p.figure}
              </p>
              <p className="text-foreground/70 font-semibold tracking-[0.2em] uppercase text-[0.7rem]">
                {p.label}
              </p>
            </div>
          ))}
        </div>
        <p className="text-center text-foreground/60 text-sm mt-10 max-w-2xl mx-auto">
          Measured results from leadership development work with leaders and teams at Amazon Web
          Services.
        </p>
      </div>
    </section>
  );
};

// ============ The Weight Lands Here - essay-anchored spine ============
const WeightLandsHere = () => {
  const heading = useScrollReveal<HTMLDivElement>();
  const body = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-foreground text-background py-32 sm:py-44 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          background:
            'radial-gradient(ellipse at 20% 20%, hsl(var(--primary) / 0.16) 0%, transparent 55%), radial-gradient(ellipse at 90% 85%, hsl(var(--sage) / 0.10) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />
      <div className="container-mobile relative z-10 max-w-4xl mx-auto">
        <div
          ref={heading.ref}
          className={`mb-12 transition-all duration-1000 ${
            heading.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-12 bg-primary" aria-hidden="true" />
            <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
              What we are learning
            </p>
          </div>
          <h2
            className="text-background text-3xl sm:text-4xl lg:text-5xl font-display leading-[1.15]"
            style={{ textWrap: 'balance' }}
          >
            The more capability flows through the system, the more weight lands on the <span className="text-primary italic">person who must stand behind what it does.</span>
          </h2>
        </div>

        <div
          ref={body.ref}
          className={`space-y-10 transition-all duration-1000 delay-150 ${
            body.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <p className="font-display italic text-2xl sm:text-3xl text-background/90 leading-snug" style={{ textWrap: 'balance' }}>
            A model can be reliable. Only a person can be <span className="text-primary">trustworthy</span>. Entire economies run on the second.
          </p>

          <div className="pt-6 border-t border-background/15 space-y-6">
            <p className="text-background/80 text-lg sm:text-xl leading-relaxed font-normal">
              Capital is flooding the technical layer. Human capacity, the scarce resource every tech company is naming, is underfunded. That is where AI initiatives stall, and where we work.
            </p>
            <Link
              to="/essays/the-human-loop"
              onClick={() => window.scrollTo(0, 0)}
              className="group inline-flex items-center gap-3 text-background font-display text-lg hover:text-primary transition-colors"
            >
              <span className="border-b border-background/30 group-hover:border-primary pb-1 transition-colors">
                Read The Human Loop
              </span>
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============ What We Believe - light editorial body ============
const WhatWeBelieve = () => {
  const heading = useScrollReveal<HTMLDivElement>();
  const body = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-background py-32 sm:py-44 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-80"
        style={{
          background:
            'radial-gradient(ellipse at 15% 10%, hsl(var(--primary) / 0.10) 0%, transparent 55%), radial-gradient(ellipse at 85% 90%, hsl(var(--sage) / 0.10) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(to right, hsl(var(--foreground)) 1px, transparent 1px)',
          backgroundSize: '120px 100%',
        }}
        aria-hidden="true"
      />

      <div className="container-mobile relative z-10 max-w-5xl mx-auto">
        <div
          ref={heading.ref}
          className={`max-w-3xl mb-16 transition-all duration-1000 ${
            heading.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-12 bg-primary" aria-hidden="true" />
            <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
              What We Believe
            </p>
          </div>
          <h2
            className="text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
            style={{ textWrap: 'balance' }}
          >
            Leadership is a <span className="text-primary italic">human practice.</span>
          </h2>
        </div>

        <div
          ref={body.ref}
          className={`grid md:grid-cols-12 gap-12 md:gap-16 items-start transition-all duration-1000 delay-150 ${
            body.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="md:col-span-7 space-y-7 text-lg sm:text-xl text-foreground/90 leading-relaxed font-normal">
            <p>
              The technical layer is commoditizing. Every serious player is converging on the same models, the same tools, the same speed. The asset that holds its value is the human depth required to carry complex outcomes while the ground keeps shifting.
            </p>
            <p>
              Capital across the industry is flowing to one pole: capability. Agents, platforms, deployment engineering. Capability funded everywhere equalizes everywhere. Meanwhile most AI initiatives stall in the same place: adoption, where the human beings are. The ground the money is passing over is the human ground. That is where we work.
            </p>
            <p>
              The competitive advantage of this moment belongs to those who can learn, develop, and adapt faster and deeper than anyone else. Faster horizontally, building new skills as the tools evolve. Deeper vertically, growing the inner capacity to hold complexity, make judgment calls under pressure, and let go of identities the work no longer asks for. Both. Compounding together.
            </p>
            <p>
              In a world becoming more artificial, the edge is becoming more fully human.
            </p>
          </div>

          <aside className="md:col-span-5 md:pl-8 md:border-l md:border-border">
            <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs mb-5">
              Our Through-Line
            </p>
            <p className="font-display italic text-2xl sm:text-3xl text-foreground leading-snug" style={{ textWrap: 'balance' }}>
              Creative potential lives in the <span className="text-primary">space between people.</span>
            </p>
            <div className="mt-8 flex items-center gap-4">
              <span className="h-px w-12 bg-primary/50" aria-hidden="true" />
              <p className="text-foreground/85 text-sm tracking-wide">
                Through practice. Over time.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

// ============ Four Dimensions - editorial grid with watermark numerals ============
const dimensions = [
  { title: 'Self', desc: 'Stay grounded in who you are when the pressure rises.', image: dimensionSelf },
  { title: 'Work', desc: 'Lead teams through complexity with clarity and judgment.', image: dimensionWork },
  { title: 'Relationships', desc: 'Be present with the people who matter most.', image: dimensionRelationships },
  { title: 'Legacy', desc: 'Build something that outlasts your tenure.', image: dimensionLegacy },
];

const DimensionCard = ({
  item,
  index,
}: {
  item: (typeof dimensions)[number];
  index: number;
}) => {
  const { ref, visible } = useScrollReveal<HTMLElement>();
  return (
    <article
      ref={ref}
      style={{ transitionDelay: visible ? `${index * 90}ms` : '0ms' }}
      className={`group relative overflow-hidden border border-background/10 hover:border-primary/40 transition-all duration-700
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
    >
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
        style={{ backgroundImage: `url(${item.image})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground via-foreground/70 to-foreground/20" />

      <div className="relative z-10 p-8 sm:p-10 pt-40 sm:pt-48 min-h-[22rem] flex flex-col justify-end">
        <div className="w-10 h-px bg-primary mb-5" aria-hidden="true" />
        <h3 className="text-background font-display text-3xl sm:text-4xl mb-3 group-hover:text-primary transition-colors duration-500">
          {item.title}
        </h3>
        <p className="text-background/75 leading-relaxed text-base">{item.desc}</p>
      </div>
    </article>
  );
};

const FourDimensions = () => {
  const heading = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-secondary/40 py-32 sm:py-44 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          background:
            'radial-gradient(ellipse at 85% 15%, hsl(var(--primary) / 0.10) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />
      <div className="container-mobile relative z-10 max-w-6xl mx-auto">
        <div
          ref={heading.ref}
          className={`max-w-3xl mb-16 transition-all duration-1000 ${
            heading.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-12 bg-primary" aria-hidden="true" />
            <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
              Fully Human Leadership
            </p>
          </div>
          <h2
            className="text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6"
            style={{ textWrap: 'balance' }}
          >
            We develop leaders across <span className="text-primary italic">all dimensions of life.</span>
          </h2>
          <p className="text-foreground/90 text-xl leading-relaxed font-normal max-w-2xl">
            You are more than your job.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-px bg-foreground/10 border border-foreground/10">
          {dimensions.map((item, i) => (
            <DimensionCard key={i} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ Compounding Practice - light contemplative ============
const Compounding = () => {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-secondary/40 text-foreground py-32 sm:py-44 overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10 pointer-events-none"
        style={{ backgroundImage: `url(${exponentialGrowthBg})` }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 pointer-events-none opacity-60"
        style={{
          background:
            'radial-gradient(ellipse at 50% 50%, hsl(var(--primary) / 0.10) 0%, transparent 60%)',
        }}
        aria-hidden="true"
      />

      <div
        ref={ref}
        className={`container-mobile relative z-10 max-w-4xl mx-auto transition-all duration-1000 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <div className="flex items-center gap-4 mb-10">
          <span className="h-px w-12 bg-primary" aria-hidden="true" />
          <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
            The ParadoxEdge Approach
          </p>
        </div>

        <p className="text-background/60 font-display italic text-lg sm:text-xl tracking-wide">
          Human capacity. Developed.
        </p>
        <h2
          className="text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-10"
          style={{ textWrap: 'balance' }}
        >
          Practice <span className="text-primary italic">compounds.</span>
        </h2>

        <p className="text-2xl sm:text-3xl text-foreground/85 leading-snug font-display italic mb-10" style={{ textWrap: 'balance' }}>
          Capability is built. Capacity is grown. Both compound over time.
        </p>
      </div>
    </section>
  );
};

// ============ Two Pathways - light editorial cards ============
const TwoPathways = () => {
  const heading = useScrollReveal<HTMLDivElement>();
  const cards = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-background py-32 sm:py-44 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-80"
        style={{
          background:
            'radial-gradient(ellipse at 10% 20%, hsl(var(--primary) / 0.10) 0%, transparent 55%), radial-gradient(ellipse at 90% 80%, hsl(var(--sage) / 0.10) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />

      <div className="container-mobile relative z-10 max-w-6xl mx-auto">
        <div
          ref={heading.ref}
          className={`max-w-3xl mb-16 transition-all duration-1000 ${
            heading.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-12 bg-primary" aria-hidden="true" />
            <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
              Who We Work With
            </p>
          </div>
          <h2
            className="text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
            style={{ textWrap: 'balance' }}
          >
            Three pathways. <span className="text-primary italic">One practice.</span>
          </h2>
          <p className="mt-6 text-foreground/90 text-lg sm:text-xl leading-relaxed font-normal max-w-2xl">
            We work with founder-led teams, growing companies, and the enterprise. Our practice has shaped leaders inside organizations from early-stage ventures to Amazon Web Services.
          </p>
        </div>

        <div
          ref={cards.ref}
          className={`grid lg:grid-cols-3 gap-6 lg:gap-8 transition-all duration-1000 delay-150 ${
            cards.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {/* For Individual Leaders - light/cream */}
          <article className="group relative bg-secondary border border-border p-10 sm:p-12 lg:p-14 flex flex-col overflow-hidden">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-11 h-11 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center">
                <User className="h-5 w-5 text-primary" strokeWidth={1.75} />
              </div>
              <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
                For Individual Leaders
              </p>
            </div>
            <h3 className="text-foreground font-display text-2xl sm:text-3xl leading-snug mb-7" style={{ textWrap: 'balance' }}>
              The leader is the leverage point.
            </h3>
            <div className="space-y-5 text-foreground/90 leading-relaxed text-base sm:text-lg font-normal flex-1">
              <p>
                You have built real capability and earned real results. The next level of impact will not come from more expertise, more hours, or more tools.
              </p>
              <p>
                It comes from who you become under pressure. From the inner capacity to stay grounded, make discerning calls, and lead in ways that no model can replicate.
              </p>
            </div>
            <div className="mt-10 pt-8 border-t border-foreground/15">
              <Link
                to="/services"
                onClick={() => window.scrollTo(0, 0)}
                className="group/link inline-flex items-center gap-3 text-foreground font-display text-lg hover:text-primary transition-colors"
              >
                <span className="border-b border-foreground/30 group-hover/link:border-primary pb-1 transition-colors">
                  Explore for Leaders
                </span>
                <ArrowRight className="h-4 w-4 group-hover/link:translate-x-1 transition-transform" />
              </Link>
            </div>
          </article>

          {/* For Organizations - dark inverted */}
          <article className="group relative bg-foreground text-background border border-foreground p-10 sm:p-12 lg:p-14 flex flex-col overflow-hidden">
            <div
              className="absolute inset-0 pointer-events-none opacity-80"
              style={{
                background:
                  'radial-gradient(ellipse at 85% 15%, hsl(var(--primary) / 0.20) 0%, transparent 55%), radial-gradient(ellipse at 10% 90%, hsl(var(--sage) / 0.10) 0%, transparent 55%)',
              }}
              aria-hidden="true"
            />
            <div className="relative z-10 flex items-center gap-4 mb-8">
              <div className="w-11 h-11 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-primary" strokeWidth={1.75} />
              </div>
              <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
                For Organizations
              </p>
            </div>
            <h3 className="relative z-10 text-background font-display text-2xl sm:text-3xl leading-snug mb-7" style={{ textWrap: 'balance' }}>
              Capacity is the operating system.
            </h3>
            <div className="relative z-10 space-y-5 text-background/75 leading-relaxed text-base sm:text-lg font-normal flex-1">
              <p>
                AI is rewriting every operating model, but technical fluency alone will not move people. Teams adopt at the speed of trust, judgment, and shared capacity.
              </p>
              <p>
                We help leadership teams and organizations develop the human infrastructure that makes transformation actually land.
              </p>
            </div>
            <div className="relative z-10 mt-10 pt-8 border-t border-background/20">
              <Link
                to="/for-organizations"
                onClick={() => window.scrollTo(0, 0)}
                className="group/link inline-flex items-center gap-3 text-background font-display text-lg hover:text-primary transition-colors"
              >
                <span className="border-b border-background/30 group-hover/link:border-primary pb-1 transition-colors">
                  Explore for Organizations
                </span>
                <ArrowRight className="h-4 w-4 group-hover/link:translate-x-1 transition-transform" />
              </Link>
            </div>
          </article>

          {/* For Companies in Transition - warm terracotta wash, distinct from cream and charcoal */}
          <article className="group relative bg-[#C45C26]/[0.08] border border-primary/30 p-10 sm:p-12 lg:p-14 flex flex-col overflow-hidden">
            <div
              className="absolute inset-0 pointer-events-none opacity-60"
              style={{
                background:
                  'radial-gradient(ellipse at 85% 15%, hsl(var(--primary) / 0.18) 0%, transparent 55%), radial-gradient(ellipse at 10% 90%, hsl(var(--primary) / 0.08) 0%, transparent 55%)',
              }}
              aria-hidden="true"
            />
            <div className="absolute top-0 inset-x-0 h-1 bg-primary/30" aria-hidden="true" />
            <div className="relative z-10 flex items-center gap-4 mb-8">
              <div className="w-11 h-11 rounded-full bg-primary/15 border border-primary/40 flex items-center justify-center">
                <Workflow className="h-5 w-5 text-primary" strokeWidth={1.75} />
              </div>
              <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
                For Companies in Transition
              </p>
            </div>
            <h3 className="relative z-10 text-foreground font-display text-2xl sm:text-3xl leading-snug mb-7" style={{ textWrap: 'balance' }}>
              AI arrives as a technology. It lands as an identity event.
            </h3>
            <div className="relative z-10 space-y-5 text-foreground/90 leading-relaxed text-base sm:text-lg font-normal flex-1">
              <p>
                You are bringing intelligence into how your company works. The workflow map is only half of what decides whether it takes.
              </p>
              <p>
                We work the human side of AI deployment, so the investment survives contact with your people.
              </p>
            </div>
            <div className="relative z-10 mt-10 pt-8 border-t border-primary/25">
              <Link
                to="/ai-transformation"
                onClick={() => window.scrollTo(0, 0)}
                className="group/link inline-flex items-center gap-3 text-foreground font-display text-lg hover:text-primary transition-colors"
              >
                <span className="border-b border-foreground/30 group-hover/link:border-primary pb-1 transition-colors">
                  Explore AI Transformation
                </span>
                <ArrowRight className="h-4 w-4 group-hover/link:translate-x-1 transition-transform" />
              </Link>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
};

// ============ Final CTA - dark with hero image ============
const FinalCTA = () => {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative py-32 sm:py-44 overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${ctaCoaching})` }}
      />
      <div className="absolute inset-0 bg-foreground/85" />
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          background:
            'radial-gradient(ellipse at 50% 50%, hsl(var(--primary) / 0.18) 0%, transparent 60%)',
        }}
        aria-hidden="true"
      />

      <div
        ref={ref}
        className={`container-mobile relative z-10 max-w-3xl mx-auto text-center space-y-10 transition-all duration-1000 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <h2
          className="text-background text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
          style={{ textWrap: 'balance' }}
        >
          What <span className="text-primary italic">now?</span>
        </h2>
        <p className="text-background/80 text-lg sm:text-xl leading-relaxed font-normal max-w-xl mx-auto">
          One conversation is where the practice begins.
        </p>
        <div className="pt-2">
          <Button
            onClick={handleScheduleCall}
            size="lg"
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-lg px-10 py-7 rounded-full transition-all duration-300 hover:shadow-xl hover:shadow-primary/20 hover:scale-105 group"
          >
            Begin a Conversation
            <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </section>
  );
};

const HomeHeroSection = () => (
  <div>
    <Hero />
    <TheMoment />
    <FeaturedTestimonial />
    <ProofBand />
    <WeightLandsHere />
    <WhatWeBelieve />
    <TwoPathways />
    <FourDimensions />
    <Compounding />
    <FinalCTA />
  </div>
);

export default HomeHeroSection;
